import { useState, useEffect, useCallback } from 'react';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Dayjs } from 'dayjs';
import {
  BankTransactionQueryParams,
  getBankTransactionsByCursor,
  getPurgedBankAccounts,
  getPurgedSecurityBankAccounts,
} from '~apis/bank';
import getBankAccounts from '~apis/bank/getBankAccounts';
import {
  dePurgeBankAccount,
  dePurgeSecurityBankAccount,
  getClobeBankAccounts,
  patchChangeBankAccountAlias,
  patchChangeSecurityBankAccountAlias,
  purgeBankAccount,
  purgeSecurityBankAccount,
  updateSecurityBankAccountPassword,
} from '~apis/cash-flow-overview/bank-account';
import { BankAccountTransaction } from '~apis/cash-flow-overview/transactions/getBankAccountTransactions';
import { BankAccountCashFlowsQueryKey } from '~hooks/queries/bank-accounts/cashFlows/query';
import { useDemoAwareCompanyId } from '~hooks/useDemoAwareCompanyId';
import { MIXPANEL_EVENT } from '~libs/mixpanel/event';
import { queryClient } from '~libs/queryClient';
import { mixpanel } from '~pages/_app';

import { DashboardQueryKey } from '../dashboard/query';
import { HomeQueryKey } from '../home/query';
import { OrganizationsQueryKey } from '../organizations/query';

interface CompanyIdentifier {
  companyId: string;
}

export const BankAccountsQueryKey = {
  default: (companyId: string) => ['bankAccounts', companyId],
  list: ({ companyId }: CompanyIdentifier) => [
    ...BankAccountsQueryKey.default(companyId),
    'list',
    companyId,
  ],
  abnormalList: (identifier: CompanyIdentifier) => [
    ...BankAccountsQueryKey.list(identifier),
    'abnormal',
  ],
  purgedList: (identifier: CompanyIdentifier) => [
    ...BankAccountsQueryKey.list(identifier),
    'purged',
  ],
  purgedSecurityList: (identifier: CompanyIdentifier) => [
    ...BankAccountsQueryKey.list(identifier),
    'purgedSecurity',
  ],
  transactionByCursor: (identifier: CompanyIdentifier, queryParams: BankTransactionQueryParams) => [
    ...BankAccountsQueryKey.list(identifier),
    'transactionByCursor',
    queryParams,
  ],
};

export function useBankAccountInfo() {
  const { data: accountListData } = useBankAccounts();
  const accountList = accountListData?.Data?.data?.accounts;
  const hasFxAccount = accountList?.some((account) => account.accountType === 'FX');
  return { hasFxAccount };
}

export function useBankAccounts() {
  const { companyId } = useDemoAwareCompanyId();
  return useQuery({
    queryKey: BankAccountsQueryKey.list({ companyId }),
    queryFn: () => getBankAccounts(companyId),
    enabled: !!companyId,
    cacheTime: 0,
  });
}

export function useClobeBankAccounts({
  baseDate,
  forLabeling,
}: {
  baseDate: Dayjs;
  forLabeling?: boolean;
}) {
  const { companyId } = useDemoAwareCompanyId();
  return useQuery({
    queryKey: [
      ...BankAccountsQueryKey.default(companyId),
      'clobe',
      baseDate.format('YYYY-MM-DD'),
      forLabeling,
    ],
    queryFn: () => getClobeBankAccounts({ companyId, baseDate, forLabeling }),
    enabled: !!companyId,
  });
}

export function useUpdateSecurityBankAccountPassword() {
  return useMutation({
    mutationFn: updateSecurityBankAccountPassword,
    onSuccess(data, variables, context) {
      queryClient.invalidateQueries(OrganizationsQueryKey.info({ companyId: variables.companyId }));
      queryClient.invalidateQueries(
        BankAccountCashFlowsQueryKey.default({ companyId: variables.companyId }),
      );
      queryClient.invalidateQueries(HomeQueryKey.dashboard({ companyId: variables.companyId }));
      queryClient.invalidateQueries(DashboardQueryKey.list({ companyId: variables.companyId }));
    },
  });
}

export function usePurgeBankAccount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: purgeBankAccount,
    onSuccess(_, variables) {
      queryClient.invalidateQueries(BankAccountsQueryKey.list(variables));
      queryClient.invalidateQueries(OrganizationsQueryKey.info({ companyId: variables.companyId }));
    },
  });
}

export function usePurgeSecurityBankAccount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: purgeSecurityBankAccount,
    onSuccess(_, variables) {
      queryClient.invalidateQueries(BankAccountsQueryKey.list(variables));
      queryClient.invalidateQueries(OrganizationsQueryKey.info({ companyId: variables.companyId }));
    },
  });
}
export function useDePurgeBankAccount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: dePurgeBankAccount,
    onSuccess(_, variables) {
      queryClient.invalidateQueries(BankAccountsQueryKey.list(variables));
      queryClient.invalidateQueries(BankAccountsQueryKey.purgedList(variables));
      queryClient.invalidateQueries(OrganizationsQueryKey.info({ companyId: variables.companyId }));
    },
  });
}

export function useDePurgeSecurityBankAccount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: dePurgeSecurityBankAccount,
    onSuccess(_, variables) {
      queryClient.invalidateQueries(BankAccountsQueryKey.list(variables));
      queryClient.invalidateQueries(BankAccountsQueryKey.purgedSecurityList(variables));
      queryClient.invalidateQueries(OrganizationsQueryKey.info({ companyId: variables.companyId }));
    },
  });
}
export function useChangeBankAccountAlias() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: patchChangeBankAccountAlias,
    onSuccess(_, variables) {
      queryClient.invalidateQueries(BankAccountsQueryKey.list(variables));
      queryClient.invalidateQueries(
        BankAccountCashFlowsQueryKey.list({ companyId: variables.companyId }),
      );
      queryClient.invalidateQueries(HomeQueryKey.dashboard({ companyId: variables.companyId }));
      queryClient.invalidateQueries(DashboardQueryKey.list({ companyId: variables.companyId }));
      queryClient.invalidateQueries(OrganizationsQueryKey.info({ companyId: variables.companyId }));
    },
  });
}

export function useChangeSecurityBankAccountAlias() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: patchChangeSecurityBankAccountAlias,
    onSuccess(_, variables) {
      queryClient.invalidateQueries(BankAccountsQueryKey.list(variables));
      queryClient.invalidateQueries(HomeQueryKey.dashboard({ companyId: variables.companyId }));
      queryClient.invalidateQueries(
        BankAccountCashFlowsQueryKey.list({ companyId: variables.companyId }),
      );
      queryClient.invalidateQueries(OrganizationsQueryKey.info({ companyId: variables.companyId }));
    },
  });
}

export function useGetPurgedBankAccounts() {
  const { companyId } = useDemoAwareCompanyId();
  return useQuery({
    queryKey: BankAccountsQueryKey.purgedList({ companyId }),
    queryFn: () => getPurgedBankAccounts({ companyId }),
    enabled: !!companyId,
  });
}

export function useGetPurgedSecurityBankAccounts() {
  const { companyId } = useDemoAwareCompanyId();
  return useQuery({
    queryKey: BankAccountsQueryKey.purgedSecurityList({ companyId }),
    queryFn: () => getPurgedSecurityBankAccounts({ companyId }),
    enabled: !!companyId,
  });
}

export function useGetBankTransactionsByCursor(queryParams: BankTransactionQueryParams) {
  const { companyId } = useDemoAwareCompanyId();
  return useQuery({
    queryKey: BankAccountsQueryKey.transactionByCursor({ companyId }, queryParams),
    queryFn: () =>
      getBankTransactionsByCursor({
        companyId,
        queryParams,
      }),
    enabled: !!companyId,
  });
}

export function useInfiniteBankTransactionsByCursor(
  baseQueryParams: Omit<
    BankTransactionQueryParams,
    'cursorDirection' | 'cursorTransactionAt' | 'cursorId'
  >,
  withNoDate?: boolean,
) {
  const { companyId } = useDemoAwareCompanyId();
  const [allTransactions, setAllTransactions] = useState<BankAccountTransaction[]>([]);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [nextCursor, setNextCursor] = useState<{ transactionAt: string; id: number } | null>(null);
  const [hasNext, setHasNext] = useState(false);

  // 초기 데이터 로드
  const initialQuery = useQuery({
    queryKey: BankAccountsQueryKey.transactionByCursor(
      { companyId },
      {
        ...baseQueryParams,
        sortOrder: 'LATEST_FIRST',
      },
    ),
    queryFn: () =>
      getBankTransactionsByCursor({
        companyId,
        queryParams: { ...baseQueryParams, sortOrder: 'LATEST_FIRST' },
      }),
    refetchOnMount: true,
    enabled: !!companyId,
  });

  // 초기 데이터가 로드되면 상태 설정
  useEffect(() => {
    if (initialQuery.data?.Data?.data) {
      const data = initialQuery.data.Data.data;
      setAllTransactions(data.content);
      setNextCursor(data.nextCursor);

      if (data.content.length > 0) {
        setNextCursor(
          data.nextCursor || {
            transactionAt: data.content[data.content.length - 1].transactionAt,
            id: data.content[data.content.length - 1].transactionId,
          },
        );
        setHasNext(data.hasNext);
      }
    }
  }, [initialQuery.data]);

  const loadMoreTransactions = useCallback(async () => {
    if (!companyId || !nextCursor || !hasNext || isLoadingMore) return;

    if (nextCursor.transactionAt === null || nextCursor.id === null) return;
    mixpanel.track(MIXPANEL_EVENT.MOBILE.SCROLL_LOAD_MORE, {
      direction: 'up',
    });
    setIsLoadingMore(true);
    try {
      const response = await getBankTransactionsByCursor({
        companyId,
        queryParams: {
          startDate: withNoDate ? undefined : baseQueryParams.startDate,
          endDate: withNoDate ? undefined : baseQueryParams.endDate,
          // cursor 기반 호출에서는 startDate, endDate 제외
          type: baseQueryParams.type,
          accounts: baseQueryParams.accounts,
          sortOrder: 'LATEST_FIRST',
          cursorTransactionAt: nextCursor.transactionAt,
          cursorId: nextCursor.id,
        },
      });

      if (response?.Data?.data) {
        const data = response.Data.data;
        setAllTransactions((prev) => [...prev, ...data.content]);
        setNextCursor(data.nextCursor);
        setHasNext(data.hasNext);
      }
    } catch (error) {
      console.error('Failed to load more transactions:', error);
    } finally {
      setIsLoadingMore(false);
    }
  }, [
    companyId,
    nextCursor,
    hasNext,
    isLoadingMore,
    baseQueryParams.type,
    baseQueryParams.accounts,
  ]);

  // 쿼리 파라미터가 변경되면 모든 상태 리셋
  useEffect(() => {
    setAllTransactions([]);
    setNextCursor(null);
    setIsLoadingMore(false);
    setHasNext(false);
  }, [JSON.stringify(baseQueryParams)]);

  return {
    data: allTransactions,
    isLoading: initialQuery.isFetching,
    isLoadingMore,
    hasNext,
    loadMoreTransactions,
    error: initialQuery.error,
  };
}
